'use client';
import Image from 'next/image';
import { useState, useEffect, useRef } from 'react';
import { Slick, SlickStyle } from '@/components/slick/slick';
import { sendGift as chatSendGift } from '@/lib/websocket';
import { sendGift as streamingSendGift } from '@/api/streaming/streaming';
import { useChatStore } from '@/store/chat';
import { useUserStore } from '@/store/user';
import { ChatGiftGroup } from '@/types/chat';
import CloseIcon from './img/close.svg';
import IconDiamond from './img/diamond.svg';
import style from './sticker.module.scss';
import { IGNORE_CHANGE_ROOM, StreamRoomState } from '@/types/streaming';
import { useValidatePhoneHook } from '@/features/validatePhone/validatePhone';
import { useWalletStore } from '@/store/wallet';
import { useDepositStore } from '@/store/deposit';
import { useNotifyStore } from '@/store/notify';
import SwipeableDrawer from '@mui/material/SwipeableDrawer/SwipeableDrawer';

type MessageContent = {
    sessionId: string;
    giftId: number;
    extra?: {
        type: string;
        video_id?: string;
        image_id?: string;
        drama_id?: string;
        episode_id?: string;
        trailer_id?: string;
    };
};

interface Props {
    isOpen: boolean;
    liveId: string;
    switchSticker: () => void;
    mode: "cp" | "user";
    fetchGetChatGiftList: (sessionId: string) => Promise<ChatGiftGroup[] | undefined>;
    tabStyle: SlickStyle;
    headerContent?: string;
    handleClose?: () => void;
    isStreamingRoom?: boolean;
}

function Sticker({ isOpen, liveId, switchSticker, mode, fetchGetChatGiftList, tabStyle, headerContent, handleClose, isStreamingRoom }: Props) {
    const isCp = useUserStore.use.isCp();
    const { checkValidatePhone } = useValidatePhoneHook();
    const [tabList, setTabList] = useState<
        ChatGiftGroup[]
    >([]);
    const onSlickEnd = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };
    const keeper = useRef('');
    const walletInfo = useWalletStore.use.walletInfo();
    const updateUserWalletInfo = useWalletStore.use.updateUserWalletInfo();
    const setDepositPopup = useDepositStore.use.setDepositPopup();
    const setSnackMessage = useNotifyStore.use.setSnackMessage();
    const isOpenSticker = useChatStore.use.isOpenSticker();
    const setIsOpenSticker = useChatStore.use.setIsOpenSticker();

    useEffect(() => {
        if (!liveId) return;
        if (keeper.current === liveId) return;
        keeper.current = liveId;
        async function handleGetGiftList() {
            const data = await fetchGetChatGiftList(liveId);
            if (!data) return;
            setTabList(data);
        }
        handleGetGiftList();
    }, [liveId]);

    const replyData = useChatStore.use.replyData();
    const resetReplyData = useChatStore.use.resetReplyData();
    const send = async (id: number, price: number) => {
        if (mode === StreamRoomState.Cp) return;
        if (walletInfo.user_diamond < price) {
            setSnackMessage({ message: "鑽石不足" });
            setDepositPopup();
            return;
        }
        const isNeedValidatePhone = checkValidatePhone();
        if (isNeedValidatePhone) return;

        const params: MessageContent = { giftId: id, sessionId: liveId };
        if (replyData.enable) {
            params.extra = {
                type: 'creation-ref',
                ...replyData
            };
            resetReplyData();
        }
        if (isStreamingRoom) {
            const res = await streamingSendGift(liveId, id);
            if (res.status !== 200) {
                setSnackMessage({ message: "鑽石不足" });
                setDepositPopup();
                return;
            }
        }
        else chatSendGift(params);
        updateUserWalletInfo(-price);
        switchSticker();
    };
    // ${ isOpen ? style.show : "" } 
    return (
        <SwipeableDrawer
            ModalProps={{
                style: {
                    pointerEvents: "none",
                    zIndex: 1000
                }
            }}
            PaperProps={{
                style: {
                    backgroundColor: "#262626",
                    maxWidth: "512px",
                    margin: "auto"
                }
            }}
            anchor="bottom"
            open={isOpenSticker}
            onOpen={() => setIsOpenSticker(true)}
            onClose={() => setIsOpenSticker(false)}
            transitionDuration={{ enter: 300, exit: 200 }}
            hideBackdrop
        >
            <div className={`${style.sticker} 
        ${isStreamingRoom ? style.streamingRoom : ""} ${IGNORE_CHANGE_ROOM}`}>
                {headerContent && <div className={style.header}>
                    <p>{headerContent}</p>
                    <CloseIcon onClick={handleClose} className={style.closeBtn} />
                </div>}

                <Slick
                    autoHeight
                    className={style.slick}
                    onSlickEnd={onSlickEnd}
                    styling={tabStyle}
                    tabs={tabList.map((item) => ({
                        label: item.categoryName,
                        status: item.categoryId,
                        href: ""
                    }))}
                >
                    {tabList.map((tab, tabIndex) => {
                        return (
                            <section
                                className={`${style.stickerList} ${isCp && style.disabled}`}
                                key={tabIndex}
                            >
                                {tab.list?.map((sticker, stickerIndex) => {
                                    return (
                                        <div
                                            className={style.itemSticker}
                                            key={stickerIndex}
                                            onClick={() => send(sticker.id, sticker.price)}
                                        >
                                            <div className={style.stickerImg}>
                                                <Image
                                                    width="56"
                                                    height="56"
                                                    src={sticker.image}
                                                    alt={sticker.name}
                                                />
                                            </div>
                                            <div className={style.name}>{sticker.name}</div>
                                            <div className={style.diamond}>
                                                <IconDiamond />
                                                <span>{sticker.price.toLocaleString()}</span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </section>
                        );
                    })}
                </Slick>
            </div>
        </SwipeableDrawer>
    );
}
export default Sticker;
